import React from "react";
import { X, ArrowUp, ArrowDown, Calendar, Hash, FileText, DollarSign } from "lucide-react";
import { formatUSD, formatDate } from "@/lib/utils";

const CATEGORY_LABEL = {
  TOPUP_HOLD: "Provider Topup",
  TOPUP_REVERSAL: "Topup Reversal",
  TOPUP_TRUEUP: "Topup True-up",
  COMMISSION: "Commission",
  TRANSFER_IN: "Transfer In",
  TRANSFER_OUT: "Transfer Out",
  FUNDING: "Funding",
  FUNDING_PAYKU: "Payku Funding",
  FUNDING_MANUAL: "Manual Funding",
  FUNDING_CARD: "Card Funding",
  WITHDRAWAL_IN: "Withdrawal In",
  WITHDRAWAL_OUT: "Withdrawal Out",
  ADJUSTMENT: "Adjustment",
};

/**
 * Detailed view of a single ledger entry. Shows previous balance (computed
 * from balance_after ± amount), the transaction amount, new balance, currency,
 * timestamp, reference id and notes.
 */
export default function TransactionDetailModal({ entry, onClose }) {
  if (!entry) return null;
  const isCredit = entry.type === "CREDIT";
  const amt = Number(entry.amount || 0);
  const balanceAfter = entry.balance_after != null ? Number(entry.balance_after) : null;
  // Ledger stores `balance_after`. Reverse it to derive previous balance.
  const prevBalance = balanceAfter == null ? null : (isCredit ? balanceAfter - amt : balanceAfter + amt);
  const label = CATEGORY_LABEL[entry.category] || (entry.category || "—").replace(/_/g, " ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/50"
      onClick={onClose}
      data-testid="txn-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div className={`px-6 py-5 ${isCredit ? "bg-emerald-50" : "bg-rose-50"}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {isCredit ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {entry.type}
              </div>
              <div className={`text-3xl font-bold mt-2 ${isCredit ? "text-emerald-700" : "text-rose-700"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {isCredit ? "+" : "−"} {formatUSD(amt)}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                <span className="inline-flex items-center gap-1 rounded-md bg-white/70 border border-slate-200 px-2 py-0.5 font-semibold">
                  {label}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="txn-detail-close"
              className="p-1.5 rounded-md text-slate-500 hover:bg-white/60 hover:text-slate-900"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          <DetailRow icon={DollarSign} label="Previous balance" value={prevBalance == null ? "—" : formatUSD(prevBalance)} testId="txn-prev-balance" />
          <DetailRow
            icon={isCredit ? ArrowUp : ArrowDown}
            label="Transaction amount"
            value={`${isCredit ? "+" : "−"} ${formatUSD(amt)}`}
            valueClass={isCredit ? "text-emerald-700" : "text-rose-700"}
            testId="txn-amount"
          />
          <DetailRow icon={DollarSign} label="New balance" value={balanceAfter == null ? "—" : formatUSD(balanceAfter)} valueClass="font-bold" testId="txn-new-balance" />
          <div className="border-t border-slate-100 pt-3 mt-3" />
          <DetailRow icon={DollarSign} label="Currency" value="USD" testId="txn-currency" />
          <DetailRow icon={Calendar} label="Date & time" value={formatDate(entry.created_at)} testId="txn-created-at" />
          <DetailRow icon={Hash} label="Reference" value={entry.ref_id ? <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{entry.ref_id}</code> : "—"} testId="txn-reference" />
          <DetailRow icon={FileText} label="Notes" value={entry.note || "—"} testId="txn-note" wrap />
          <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            Ledger entry id: <code className="bg-slate-50 px-1 rounded">{entry.id}</code>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, valueClass = "", testId, wrap }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div data-testid={testId} className={`text-sm text-slate-900 mt-0.5 ${valueClass} ${wrap ? "break-words" : ""}`}>
          {value}
        </div>
      </div>
    </div>
  );
}
